import { useState } from "react";
import { BarChartDay, BarChartMonth, BarChartYear } from '../../../components/EnergyBarCharts'
import { CostsAndCO2Panel } from '../BaseCounter/CostsAndCO2Panel'
import { OperatorPanelWrapperFullSize } from '../../components/Layout'
import { ICounter } from './types'
import { useGetMediumColor } from '../../../hooks/useGetMediumColor'
import { FormattedMessage, useIntl } from "react-intl";
import { Block } from "baseui/block";
import { ButtonGroup, MODE } from "baseui/button-group";
import { Button } from "baseui/button";
import { BsCalendar2Month, BsCalendar3, BsCalendarDate } from "react-icons/bs";
import { AiOutlineLineChart } from "react-icons/ai";
import { CounterDisabledInfo } from "./CounterDisabledInfo";
import { useStyletron } from "baseui";
import { TimeSeriesLineChart } from "../../../features/Charts/LineCharts/TimeSeriesLineChart";
import useIsMobile from "../../../hooks/useIsMobile/useIsMobile";
import { ValueDisplayWithLabel } from "../../components/ValueDisplay";
import { themedUseStyletron } from "../../../theme/customTheme";
import { RelatedPlants } from "../BaseCounter/RealtedPlants";
import { StyledDivider } from "baseui/divider";
import { useGetCounterRates } from "../../../hooks/useGetCounterRates";
import { Notification } from 'baseui/notification'
import { Link } from "react-router-dom";
import { StyledLink } from "baseui/link";
import { GetObjectUrl } from "../../../utils/navigationService";
import { Banner } from "baseui/banner";
import { VerticalStackPanel } from "../../../components/Layout/StackPanel";
import { getMediumColor } from "../../../utils/colorUtils";

export const OperatorPanel = (props: ICounter) => {
    const [css, theme] = themedUseStyletron();
    const color = getMediumColor(props.counterMedium, props.counterType,theme);
    const [activeKey, setActiveKey] = useState<number>(0);
    const intl = useIntl();
    const isMobile = useIsMobile();
    const { count: countRates, isSuccess: isSuccessRates } = useGetCounterRates(props.id, props.resourceGroupId);
    const chartHeight = isMobile ? '450px' : '600px';
    const dimPower = props.dimension === 'kWh' ? 'kW' : props.dimension === 'MWh' ? 'kW' : props.dimension === 'Wh' ? 'W' : props.dimension === 'm³' ? 'l' : props.dimension === 'l' ? 'l' : '[]'
    return (
        <OperatorPanelWrapperFullSize
            objectId={props.id}
            resourceGroupId={props.resourceGroupId}
            statusContent={
                <div  className={css({ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'center' })}>
                    {isSuccessRates && countRates <= 1 && <Banner overrides={{
                        Root: {
                            style: { width: '100%' }
                        }
                    }} kind="warning" >
                        <FormattedMessage id="Counter.RatesMissing" defaultMessage="Add links to rates to get more information about Cost and CO2" />
                        <span> </span>
                        <Link to={`${GetObjectUrl(props.resourceGroupId, props.id)}?defaultView=configuration`} className={css({ textDecoration: 'none' })}>
                            <StyledLink animateUnderline ><FormattedMessage id="configuration" defaultMessage="Configuration" /></StyledLink>
                        </Link></Banner>}
                    <CounterDisabledInfo {...props}></CounterDisabledInfo>
                    <div className={css({ height: '80px'  })}>
                        {activeKey === 0 && <CostsAndCO2Panel currencyCode={props.currencyCode} id={props.id} resourceGroupId={props.resourceGroupId} counterType={props.counterType} dimension={props.dimension} days={7}></CostsAndCO2Panel>}
                        {activeKey === 1 && <CostsAndCO2Panel currencyCode={props.currencyCode} id={props.id} resourceGroupId={props.resourceGroupId} counterType={props.counterType} dimension={props.dimension} days={365}></CostsAndCO2Panel>}
                        {activeKey === 2 && <CostsAndCO2Panel currencyCode={props.currencyCode} id={props.id} resourceGroupId={props.resourceGroupId} counterType={props.counterType} dimension={props.dimension} days={5 * 365}></CostsAndCO2Panel>}
                        {activeKey === 3 && <ValueDisplayWithLabel  color={theme.appColors.aggregate.off} label={intl.formatMessage({ id: "Devices.ActPower", defaultMessage: "Act Power" })} value={props.actPower} dimension={dimPower}></ValueDisplayWithLabel>}
                    </div>
                </div>
            }
            operationModeContent={
                <VerticalStackPanel>
                    <Block alignSelf={'center'}>
                        <ButtonGroup
                            size='mini'
                            mode={MODE.radio}
                            kind='secondary'
                            selected={activeKey}
                            onClick={(event, index) => {
                                setActiveKey(index);
                            }}>
                            <Button title={intl.formatMessage({ id: "Devices.Days", defaultMessage: "Days" })} startEnhancer={() => <BsCalendarDate></BsCalendarDate>}>{intl.formatMessage({ id: "Devices.Days", defaultMessage: "Days" })}</Button>
                            <Button title={intl.formatMessage({ id: "Devices.Months", defaultMessage: "Months" })} startEnhancer={() => <BsCalendar2Month></BsCalendar2Month>}><FormattedMessage id='Devices.Months' defaultMessage={'Months'}></FormattedMessage></Button>
                            <Button title={intl.formatMessage({ id: "Devices.Years", defaultMessage: "Years" })} startEnhancer={() => <BsCalendar3></BsCalendar3>}>{intl.formatMessage({ id: "Devices.Years", defaultMessage: "Years" })}</Button>
                            <Button title={intl.formatMessage({ id: "Devices.Trend", defaultMessage: "Trend" })} startEnhancer={() => <AiOutlineLineChart></AiOutlineLineChart>}>{intl.formatMessage({ id: "Devices.Trend", defaultMessage: "Trend" })} </Button>
                        </ButtonGroup>
                    </Block>
                    <div className={css({ minHeight: '300px', height: chartHeight, width: '100%' })}>
                        {activeKey === 0 && <BarChartDay resourceGroupId={props.resourceGroupId} color={color} responsive={true} id={props.id} objectDescription={props.description} ></BarChartDay>}
                        {activeKey === 1 && <BarChartMonth resourceGroupId={props.resourceGroupId} color={color} responsive={true} id={props.id} objectDescription={props.description} ></BarChartMonth>}
                        {activeKey === 2 && <BarChartYear resourceGroupId={props.resourceGroupId} color={color} responsive={true} id={props.id} objectDescription={props.description} ></BarChartYear>}
                        {activeKey === 3 &&
                            <TimeSeriesLineChart lines={[{
                                label: intl.formatMessage({ id: "Devices.ActPower", defaultMessage: "Act Power" }),
                                lineColor: color,
                                objectId: props.id,
                                resourceGroupId: props.resourceGroupId,
                                propertyName: 'actPower',
                                currentValue: props.actPower,
                                showPoints: false,
                                fill: true,
                                yScaleId: 'y',
                                showMinMax: true
                            }]}
                                enableZoom
                                summarizeBy={'10m'}
                                timespan={24 * 60 * 7}
                                showTimeNavigation
                                showAverage
                                showLegend ></TimeSeriesLineChart>
                        }
                    </div>

                </VerticalStackPanel>
            }
        >
            {isMobile &&
                <div className={css({ width: '100%' })}>
                    <StyledDivider $size={'cell'} ></StyledDivider>
                    <RelatedPlants {...props}></RelatedPlants>
                </div>}
        </OperatorPanelWrapperFullSize>
    )

}

